import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  fetchResources,
  createResource,
} from '../../actions/planningAction';
import './PlanningLibrary.css';

// Flat list of all nodes for the link picker + crumb lookup.
// Recurses through both .actions and .subNodes at any depth so nested nodes
// (e.g. actions under actions) are never missed.
export function flattenNodes(milestones) {
  const out = [];
  const walk = (node, crumb) => {
    out.push({ _id: String(node._id), content: node.content, type: node.type || 'node', crumb });
    for (const a of (node.actions || [])) walk(a, node.content);
    for (const s of (node.subNodes || [])) walk(s, node.content);
  };
  for (const m of (milestones || [])) walk(m, null);
  return out;
}

// Strip markdown to clean, left-aligned plain text for card previews
function plainPreview(md) {
  if (!md) return '';
  return md
    .replace(/```[\s\S]*?```/g, ' ')        // code blocks
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')   // images
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // links → text
    .replace(/[#>*_`~|-]/g, ' ')             // md symbols
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 180);
}

function relTime(d) {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── Link picker ─────────────────────────────────────────────────────────────
export function LinkPicker({ allNodes, selected, onToggle, onClose }) {
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);
  const filtered = q.trim()
    ? allNodes.filter(n => n.content?.toLowerCase().includes(q.toLowerCase()))
    : allNodes;
  return (
    <div className="pl-linkpick" ref={ref}>
      <input className="pl-linkpick-search" placeholder="Link to nodes…" value={q} onChange={e => setQ(e.target.value)} autoFocus />
      <div className="pl-linkpick-list">
        {filtered.slice(0, 40).map(n => {
          const on = selected.includes(n._id);
          return (
            <button key={n._id} className={`pl-linkpick-item${on ? ' pl-linkpick-item--on' : ''}`} onClick={() => onToggle(n._id)}>
              <span className={`pl-node-tag pl-node-tag--${n.type}`}>{n.type}</span>
              <span className="pl-linkpick-content">{n.content}</span>
              <span className="pl-linkpick-check">{on ? '✓' : ''}</span>
            </button>
          );
        })}
        {filtered.length === 0 && <div className="pl-linkpick-empty">No matching nodes</div>}
      </div>
    </div>
  );
}


// ── Library ─────────────────────────────────────────────────────────────────
export default function PlanningLibrary({ visionId, milestones, theme }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [query,     setQuery]     = useState('');
  const [creating,  setCreating]  = useState(false);

  const allNodes = useMemo(() => flattenNodes(milestones), [milestones]);
  const nodeById = useMemo(() => Object.fromEntries(allNodes.map(n => [n._id, n])), [allNodes]);

  const openDoc = (id) => navigate(`/dashboard/planning/${visionId}/doc/${id}`);

  const load = useCallback(() => {
    setLoading(true);
    dispatch(fetchResources(visionId))
      .then(d => setResources(d.resources || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dispatch, visionId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const d = await dispatch(createResource(visionId, { title: 'Untitled', body: '', linkedNodeIds: [] }));
      openDoc(d.resource._id);
    } catch (_) {}
    setCreating(false);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return resources;
    return resources.filter(r =>
      (r.title || '').toLowerCase().includes(q) ||
      (r.body || '').toLowerCase().includes(q)
    );
  }, [resources, query]);

  return (
    <div className={`pl-wrap${theme === 'light' ? ' pl-wrap--light' : ''}`}>
      <div className="pl-inner">
        <div className="pl-header">
          <div>
            <h1 className="pl-heading">Library</h1>
            <p className="pl-sub">Every document and resource for this vision. One doc can serve many nodes.</p>
          </div>
          <button className="pl-new-btn" onClick={create} disabled={creating}>
            {creating ? <><span className="pl-spinner" /> Creating…</> : '+ New document'}
          </button>
        </div>

        <input
          className="pl-search"
          placeholder="Search documents…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />

        {loading ? (
          <div className="pl-empty"><span className="pl-spinner pl-spinner--lg" /> Loading documents…</div>
        ) : filtered.length === 0 ? (
          <div className="pl-empty">
            {query ? 'No documents match.' : 'No documents yet. Create one, or draft from a node.'}
          </div>
        ) : (
          <div className="pl-grid">
            {filtered.map(r => (
              <button key={r._id} className="pl-card" onClick={() => openDoc(r._id)}>
                <div className="pl-card-head">
                  <span className="pl-card-title">{r.title || 'Untitled'}</span>
                  <span className="pl-card-time">{relTime(r.lastActiveAt)}</span>
                </div>
                <div className="pl-card-snippet">
                  {plainPreview(r.body) || <span className="pl-card-snippet-empty">Empty document</span>}
                </div>
                {(r.linkedNodeIds || []).length > 0 && (
                  <div className="pl-card-links">
                    {r.linkedNodeIds.slice(0, 3).map(id => {
                      const n = nodeById[String(id)];
                      return (
                        <span key={String(id)} className="pl-card-link-chip">
                          <span className={`pl-node-dot pl-node-dot--${n?.type || 'node'}`} />
                          {n ? (n.content.length > 24 ? n.content.slice(0, 24) + '…' : n.content) : 'node'}
                        </span>
                      );
                    })}
                    {r.linkedNodeIds.length > 3 && <span className="pl-card-link-more">+{r.linkedNodeIds.length - 3}</span>}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
